import { motion, type TargetAndTransition } from "framer-motion"
import { memo } from "react"

interface HeroSportEmojiProps {
  sport: {
    emoji: string
    name: string
    isImage?: boolean
  }
  index: number
  x: number
  y: number
  isScrolling: boolean
  isMdLgOrSmaller: boolean
  activeSport: string | null
  isPlayingSlogan: boolean
  selectedSportForSequence: string | null
  isAtTop: boolean
  imageErrors: Set<string>
  imageLoaded: Set<string>
  onSportClick: (sport: { emoji: string; name: string; isImage?: boolean }) => void
  onImageError: (emoji: string) => void
  onImageLoad: (emoji: string) => void
  onFootballHover: (hovering: boolean) => void
}

export const HeroSportEmoji = memo(function HeroSportEmoji({
  sport,
  index,
  x,
  y,
  isScrolling,
  isMdLgOrSmaller,
  activeSport,
  isPlayingSlogan,
  selectedSportForSequence,
  isAtTop,
  imageErrors,
  imageLoaded,
  onSportClick,
  onImageError,
  onImageLoad,
  onFootballHover,
}: HeroSportEmojiProps) {
  const getBaseScale = () => {
    const base =
      sport.name === "hockey"
        ? isScrolling
          ? 0.85 * 1.08
          : 1.08
        : sport.name === "football"
          ? isScrolling
            ? 0.85 * 0.918
            : 0.918
          : sport.name === "boxing"
            ? isScrolling
              ? 0.85 * 0.945
              : 0.945
            : sport.name === "golf"
              ? isScrolling
                ? 0.85 * 0.927
                : 0.927
              : isScrolling
                ? 0.765
                : 0.9

    const responsiveBase = isMdLgOrSmaller ? base * 1.07 : base * 1.01

    if (activeSport === sport.name || (isPlayingSlogan && selectedSportForSequence === sport.name)) {
      if (sport.name === "hockey") return responsiveBase * 1.1
      return isMdLgOrSmaller ? 0.99 * 1.07 : 0.99 * 1.01
    }

    return responsiveBase
  }

  const getFilter = () => {
    if (activeSport === sport.name || (isPlayingSlogan && selectedSportForSequence === sport.name)) {
      return [
        "brightness(1.1) drop-shadow(0 0 12px rgba(255,248,240,0.35))",
        "brightness(1.17) drop-shadow(0 0 17px rgba(255,248,240,0.45))",
        "brightness(1.12) drop-shadow(0 0 15px rgba(255,248,240,0.4))",
        "brightness(1.2) drop-shadow(0 0 20px rgba(255,248,240,0.5))",
        "brightness(1.1) drop-shadow(0 0 12px rgba(255,248,240,0.35))",
      ]
    }
    return isScrolling ? "blur(2px)" : "blur(0px)"
  }

  const getHoverState = (): TargetAndTransition => {
    if (sport.name === "football") {
      return {
        scale: isMdLgOrSmaller ? 0.99 * 1.07 : 0.99 * 1.01,
        filter: [
          "brightness(1.0825) drop-shadow(0 0 20px rgba(255,248,240,0.45))",
          "brightness(1.1575) drop-shadow(0 0 30px rgba(255,248,240,0.6))",
          "brightness(1.0975) drop-shadow(0 0 25px rgba(255,248,240,0.525))",
          "brightness(1.1725) drop-shadow(0 0 35px rgba(255,248,240,0.675))",
          "brightness(1.0825) drop-shadow(0 0 20px rgba(255,248,240,0.45))",
        ],
        transition: {
          scale: { type: "spring", bounce: 0.6 },
          filter: {
            duration: 0.6,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "loop",
            ease: "easeInOut",
          },
        },
      }
    }
    if (sport.name === "hockey") {
      return {
        scale: isMdLgOrSmaller ? 1.188 * 1.07 : 1.188 * 1.01,
        filter: "brightness(1.075) drop-shadow(0 0 25px rgba(255,248,240,0.525))",
        transition: { type: "spring", bounce: 0.6 },
      }
    }
    return {
      scale: isMdLgOrSmaller ? 0.99 * 1.07 : 0.99 * 1.01,
      filter: "brightness(1.075) drop-shadow(0 0 25px rgba(255,248,240,0.525))",
      transition: { type: "spring", bounce: 0.6 },
    }
  }

  const shouldHide = index === 4 && isAtTop

  return (
    <motion.div
      className={`absolute select-none cursor-pointer pointer-events-auto ${shouldHide ? "md:hidden short:hidden" : ""}`}
      style={{
        fontSize: isMdLgOrSmaller ? "2.8147687rem" : "2.6569312rem",
      }}
      animate={{
        scale: getBaseScale(),
        x,
        y: isScrolling ? y : [y - 2, y + 2, y - 2],
        filter: getFilter(),
      }}
      transition={{
        y: isScrolling
          ? { duration: 0.3 }
          : {
              duration: 3 + index * 0.3,
              delay: index * 0.05,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: "reverse",
              ease: "easeInOut",
            },
        filter:
          activeSport === sport.name || (isPlayingSlogan && selectedSportForSequence === sport.name)
            ? { duration: 0.6, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }
            : { duration: 0.3, ease: "easeOut" },
      }}
      whileHover={getHoverState()}
      whileTap={{
        scale: isMdLgOrSmaller ? 0.765 * 1.07 : 0.765 * 1.01,
        transition: { type: "spring", bounce: 0.6 },
      }}
      onClick={() => onSportClick(sport)}
      onMouseEnter={() => sport.name === "football" && onFootballHover(true)}
      onMouseLeave={() => sport.name === "football" && onFootballHover(false)}
      onMouseDown={() => sport.name === "football" && onFootballHover(true)}
      onMouseUp={() => sport.name === "football" && onFootballHover(false)}
      onTouchStart={() => sport.name === "football" && onFootballHover(true)}
      onTouchEnd={() => sport.name === "football" && onFootballHover(false)}
    >
      {sport.isImage ? (
        imageErrors.has(sport.emoji) ? (
          <span className="text-[2.8147687rem] lg:text-[2.6569312rem]">
            {sport.name === "football"
              ? "🏈"
              : sport.name === "baseball"
                ? "⚾"
                : sport.name === "boxing"
                  ? "🥊"
                  : sport.name === "basketball"
                    ? "🏀"
                    : sport.name === "soccer"
                      ? "⚽"
                      : sport.name === "hockey"
                        ? "🏒"
                        : "🏈"}
          </span>
        ) : (
          <img
            src={sport.emoji}
            alt={sport.name}
            className="w-[2.8147687rem] h-[2.8147687rem] lg:w-[2.6569312rem] lg:h-[2.6569312rem] object-contain"
            draggable={false}
            onError={() => onImageError(sport.emoji)}
            onLoad={() => onImageLoad(sport.emoji)}
            style={{
              filter: !imageLoaded.has(sport.emoji) ? "opacity(0.5)" : "none",
              transition: "filter 0.3s ease-out",
            }}
          />
        )
      ) : (
        sport.emoji
      )}
    </motion.div>
  )
})
